import React from 'react';
import { authorInfo } from '../mock';
import { Linkedin, Github, Globe } from 'lucide-react';
import SEO, { buildPersonJsonLd, buildBreadcrumbJsonLd } from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen pt-32 pb-24 px-6">
      <SEO
        title="About Riddhi Tikoo · 10th-Grade AI Ethics Researcher"
        description="Riddhi Tikoo is a 10th-grade AI ethics researcher and student developer in the Greater Seattle Area. Founder of SafePlayAI, building tools and lessons that make responsible AI accessible to high schoolers."
        path="/about"
        keywords={['Riddhi Tikoo','teen AI researcher','SafePlayAI','young AI advocate','high school AI ethics']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [buildPersonJsonLd(), buildBreadcrumbJsonLd([{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }])],
        }}
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-light mb-6">About Me</h1>
          <p className="text-xl text-gray-600">
            Teenager, Coder, Ethical AI Advocate
          </p>
        </div>

        {/* Author Profile */}
        <div className="flex flex-col md:flex-row gap-12 mb-16">
          {/* Photo */}
          <div className="flex-shrink-0">
            <img
              src={authorInfo.image}
              alt={authorInfo.name}
              className="w-64 h-64 rounded-lg object-cover mx-auto md:mx-0"
            />
          </div>

          {/* Info */}
          <div className="flex-1">
            <h2 className="text-3xl font-light mb-2">{authorInfo.name}</h2>
            <p className="text-gray-600 mb-2">{authorInfo.title}</p>
            <p className="text-gray-500 text-sm mb-2">{authorInfo.school}</p>
            <p className="text-gray-500 text-sm mb-6">{authorInfo.location}</p>
            
            {/* Social Links */}
            <div className="flex space-x-4 mb-8">
              <a
                href={authorInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 hover:-translate-y-1 transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href={authorInfo.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 hover:-translate-y-1 transition-all"
                aria-label="GitHub"
              >
                <Github size={20} />
              </a>
              <a
                href={authorInfo.social.website}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 hover:-translate-y-1 transition-all"
                aria-label="Website"
              >
                <Globe size={20} />
              </a>
            </div>

            {/* Bio */}
            <p className="text-gray-700 leading-relaxed text-lg">
              {authorInfo.bio}
            </p>
          </div>
        </div>

        {/* Story Section */}
        <div className="prose prose-lg max-w-none">
          <h2 className="text-3xl font-light mb-6">My Story</h2>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            I wrote my first line of code when I was 7 years old. What started as 
            curiosity about how video games worked turned into a decade-long journey 
            into programming, AI, and ethical technology development.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            I'm currently a 10th grader at Redmond High School in the Greater Seattle Area, 
            and I'm the Founder/CEO of SafePlayAI. I've built 4 apps, participated in programs 
            like Stanford AI4ALL and LaunchX, and interned at the Washington State House of 
            Representatives.
          </p>

          <h3 className="text-2xl font-light mb-4 mt-12">The Supervoices.co Turning Point</h3>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            My first profitable app, Supervoices.co, was making over $100 a month helping 
            students with dyslexia convert text to audio. But when I realized the technology 
            could be exploited for unauthorized voice cloning, I made the hardest decision 
            of my life: I shut it down.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            That experience taught me that technical innovation must always come after 
            ethical responsibility. It shaped everything I've built since.
          </p>

          <h3 className="text-2xl font-light mb-4 mt-12">What I'm Building Now</h3>
          
          <ul className="space-y-3 mb-6 ml-6">
            <li className="text-gray-700">
              <strong>SafePlayAI</strong> – Creating safe, bounded environments for AI use 
              in Washington schools
            </li>
            <li className="text-gray-700">
              <strong>Prepwise</strong> – AI-assisted test preparation with academic 
              integrity safeguards
            </li>
            <li className="text-gray-700">
              <strong>SentryPulse</strong> – AI sentiment analysis for protecting youth 
              from online predators and cyberbullying
            </li>
          </ul>

          <h3 className="text-2xl font-light mb-4 mt-12">My Mission</h3>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            I believe that the people who are crazy enough to think they can change the 
            world are the ones who actually do. As Steve Jobs said—and I've taken to heart.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            My generation doesn't just use the internet—we live in it. And that environment 
            is increasingly dangerous, especially for young people. I'm not waiting for 
            adults to figure out the solutions. I'm building them myself.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Through my work with SafePlayAI, my internship at the Washington State House, 
            and my involvement in AI education initiatives, I've learned that technology 
            policy and technology development can't be separated. We need clear rules, 
            shared standards, and thoughtful oversight to ensure innovation serves the 
            public good.
          </p>

          <h3 className="text-2xl font-light mb-4 mt-12">Beyond Tech</h3>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            I'm also passionate about diversity, equity, and inclusion. As Secretary and 
            former DEI Team Leader at Indian American Community Services (IACS), I've 
            learned that building inclusive technology requires inclusive teams and 
            perspectives.
          </p>

          <h3 className="text-2xl font-light mb-4 mt-12">Why I Write</h3>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            This blog is my way of documenting the journey—the wins, the failures, the 
            ethical dilemmas, and the lessons learned. I write to:
          </p>

          <ul className="space-y-3 mb-6 ml-6">
            <li className="text-gray-700">Process my own thinking about complex ethical questions</li>
            <li className="text-gray-700">Make AI ethics accessible to other teenagers and students</li>
            <li className="text-gray-700">Show that you don't need to be older, richer, or more experienced to make a difference</li>
            <li className="text-gray-700">Inspire the next generation of ethical builders</li>
          </ul>

          <h3 className="text-2xl font-light mb-4 mt-12">Let's Connect</h3>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            I'm always eager to connect with fellow students, researchers, educators, and 
            anyone passionate about ethical technology. Whether you want to collaborate, 
            share ideas, or just chat about AI ethics, I'd love to hear from you.
          </p>

          <p className="text-gray-700 leading-relaxed">
            Reach out via email, connect with me on LinkedIn, or check out SafePlayAI at{' '}
            <a 
              href={authorInfo.social.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-black underline hover:no-underline"
            >
              safeplayai.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
